<!--
  DashboardDefault.vue — Phase 1.4: Command Center Dashboard
  
  Live widgets:
    - Fleet Status Donut (ECharts)
    - Active vs Inactive Assets
    - Recent Alerts (24h)
    - Agent Health Cards (one per agent)
    - Mini Narrative Feed (last 10 events, compact)
-->
<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/DeviceStore'
import { useSocket, Events, connectSocket, socketState } from '@/services/SocketService'
import { AgentStatusCard, NarrativeFeed } from '@/components/sensando'
import { useTheme } from 'vuetify'
import { resolveDisplayState } from '@/@core/utils/displayState'

const { t } = useI18n()

// ECharts setup (tree-shaken)
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import VChart from 'vue-echarts'

use([CanvasRenderer, PieChart, TooltipComponent, LegendComponent])

const deviceStore = useDeviceStore()
const theme = useTheme()
const { on, isConnected } = useSocket()

/* ─── Reactive dashboard state ────────────────────────────────── */
const loading = ref(true)
const assets = ref([])
const recentAlerts = ref([])
const alertCount24h = ref(0)

/* ─── Agent health state ──────────────────────────────────────── */
const agentHealth = reactive({
  centinela: {
    status: 'monitoring',
    summary: t('dashboard.agentSummary.centinela'),
    eventsToday: 0,
    lastAction: null,
  },
  jefe: {
    status: 'active',
    summary: t('dashboard.agentSummary.jefe'),
    eventsToday: 0,
    lastAction: null,
  },
  supervisor: {
    status: 'idle',
    summary: t('dashboard.agentSummary.supervisor'),
    eventsToday: 0,
    lastAction: null,
  },
  ingeniero: {
    status: 'idle',
    summary: t('dashboard.agentSummary.ingeniero'),
    eventsToday: 0,
    lastAction: null,
  },
})

/* ─── Fleet status computed ───────────────────────────────────── */
const fleetStatus = computed(() => {
  const counts = { moving: 0, idle: 0, stopped: 0, offline: 0, alert: 0 }
  assets.value.forEach(a => {
    const s = resolveDisplayState(a)
    if (counts[s] !== undefined) counts[s]++
  })
  return counts
})

const totalAssets = computed(() => assets.value.length)
const activeAssets = computed(() => totalAssets.value - fleetStatus.value.offline)

/* ─── ECharts donut config ────────────────────────────────────── */
const donutOption = computed(() => {
  const isDark = theme.global.current.value.dark
  const fs = fleetStatus.value
  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: {
      bottom: 0,
      textStyle: { color: isDark ? '#94A3B8' : '#64748B', fontSize: 11 },
    },
    series: [{
      type: 'pie',
      radius: ['55%', '80%'],
      center: ['50%', '45%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 6, borderColor: isDark ? '#1E293B' : '#ffffff', borderWidth: 2 },
      label: {
        show: true,
        position: 'center',
        formatter: () => `${totalAssets.value}\n${t('dashboard.assets')}`,
        fontSize: 16,
        fontWeight: 600,
        color: isDark ? '#E2E8F0' : '#1E293B',
        lineHeight: 22,
      },
      emphasis: {
        label: { show: true, fontSize: 18 },
        itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' },
      },
      data: [
        { value: fs.moving,  name: t('dashboard.fleetLabels.moving'),  itemStyle: { color: '#22C55E' } },
        { value: fs.idle,    name: t('dashboard.fleetLabels.idle'),     itemStyle: { color: '#F59E0B' } },
        { value: fs.stopped, name: t('dashboard.fleetLabels.stopped'),  itemStyle: { color: '#94A3B8' } },
        { value: fs.alert,   name: t('dashboard.fleetLabels.alert'),    itemStyle: { color: '#EF4444' } },
        { value: fs.offline, name: t('dashboard.fleetLabels.offline'),  itemStyle: { color: '#475569' } },
      ].filter(d => d.value > 0),
    }],
  }
})

/* ─── Socket listeners ────────────────────────────────────────── */
function setupSocketListeners() {
  connectSocket()

  on(Events.DEVICE_MEASUREMENT, (payload) => {
    const id = payload.device_id || payload.asset_id
    const idx = assets.value.findIndex(a => a._id === id)
    if (idx !== -1) {
      const parsed = payload._parsed || {}
      assets.value[idx].speed = parsed.speed ?? assets.value[idx].speed
      assets.value[idx].heading = parsed.heading ?? assets.value[idx].heading
      assets.value[idx].lastSeen = new Date().toISOString()
    }
    agentHealth.jefe.eventsToday++
  })

  on(Events.DEVICE_EVENT, (payload) => {
    const id = payload.device_id || payload.asset_id
    const idx = assets.value.findIndex(a => a._id === id)
    if (idx !== -1) assets.value[idx]._alert = true

    alertCount24h.value++
    recentAlerts.value.unshift({
      id: payload.id || Date.now(),
      title: payload.title || payload.message || t('dashboard.alert'),
      severity: payload.severity || 'high',
      asset: payload.device_name || payload.asset_name || id,
      timestamp: new Date().toISOString(),
    })
    if (recentAlerts.value.length > 10) recentAlerts.value.pop()

    // Route to appropriate agent
    const agent = resolveAlertAgent(payload)
    agentHealth[agent].eventsToday++
    agentHealth[agent].lastAction = {
      text: t('dashboard.alertPrefix', { title: payload.title || payload.message || t('dashboard.event') }),
      timestamp: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
    }
  })

  on(Events.DEVICE_STATUS, (payload) => {
    const id = payload.device_id || payload.asset_id
    const idx = assets.value.findIndex(a => a._id === id)
    if (idx !== -1) assets.value[idx].status = payload.status
  })

  on(Events.AGENT_STATUS, (payload) => {
    const agent = payload.agent || payload.agent_name
    if (agentHealth[agent]) {
      agentHealth[agent].status = payload.status
      agentHealth[agent].summary = payload.summary || agentHealth[agent].summary
    }
  })
}

function resolveAlertAgent(payload) {
  const type = (payload.type || payload.event_type || '').toLowerCase()
  if (type.includes('security') || type.includes('intrusion') || type.includes('camera')) return 'centinela'
  if (type.includes('safety') || type.includes('process')) return 'supervisor'
  if (type.includes('energy') || type.includes('fluid') || type.includes('ambiance') || type.includes('access') || type.includes('network') || type.includes('infrastructure')) return 'ingeniero'
  return 'centinela' // alerts default to centinela
}

/* ─── Load initial data ───────────────────────────────────────── */
onMounted(async () => {
  try {
    const devices = await deviceStore.fetchDevices()
    assets.value = (devices || []).map(d => ({
      ...d,
      speed: d.speed ?? 0,
      heading: d.heading ?? 0,
      _alert: false,
    }))
  } catch (e) {
    console.error('[Dashboard] Error loading devices:', e)
  } finally {
    loading.value = false
  }

  setupSocketListeners()
})
</script>

<template>
  <div>
    <!-- Connection status banner -->
    <VAlert
      v-if="!isConnected"
      type="warning"
      variant="tonal"
      density="compact"
      class="mb-4"
      closable
    >
      <template #prepend>
        <VIcon icon="mdi-wifi-off" />
      </template>
      {{ t('dashboard.connectionLost') }}
    </VAlert>

    <!-- Loading skeleton -->
    <div v-if="loading" class="d-flex flex-column gap-4">
      <VRow>
        <VCol v-for="i in 4" :key="i" cols="12" sm="6" lg="3">
          <VSkeletonLoader type="card" />
        </VCol>
      </VRow>
    </div>

    <!-- Dashboard grid -->
    <div v-else>
      <!-- Row 1: KPI Cards -->
      <VRow class="mb-1">
        <!-- Total Assets -->
        <VCol cols="12" sm="6" lg="3">
          <VCard rounded="lg">
            <VCardText class="d-flex align-center gap-4">
              <VAvatar color="primary" variant="tonal" size="48" rounded>
                <VIcon size="28" icon="mdi-truck" />
              </VAvatar>
              <div>
                <div class="text-h4 font-weight-bold">{{ totalAssets }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ t('dashboard.totalAssets') }}</div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Active Assets -->
        <VCol cols="12" sm="6" lg="3">
          <VCard rounded="lg">
            <VCardText class="d-flex align-center gap-4">
              <VAvatar color="success" variant="tonal" size="48" rounded>
                <VIcon size="28" icon="mdi-access-point" />
              </VAvatar>
              <div>
                <div class="text-h4 font-weight-bold text-success">{{ activeAssets }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ t('dashboard.activeNow') }}</div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Offline -->
        <VCol cols="12" sm="6" lg="3">
          <VCard rounded="lg">
            <VCardText class="d-flex align-center gap-4">
              <VAvatar color="secondary" variant="tonal" size="48" rounded>
                <VIcon size="28" icon="mdi-power-plug-off" />
              </VAvatar>
              <div>
                <div class="text-h4 font-weight-bold">{{ fleetStatus.offline }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ t('dashboard.offline') }}</div>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Alerts 24h -->
        <VCol cols="12" sm="6" lg="3">
          <VCard rounded="lg">
            <VCardText class="d-flex align-center gap-4">
              <VAvatar color="error" variant="tonal" size="48" rounded>
                <VIcon size="28" icon="mdi-alert-circle" />
              </VAvatar>
              <div>
                <div class="text-h4 font-weight-bold text-error">{{ alertCount24h }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ t('dashboard.alerts24h') }}</div>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!-- Row 2: Donut chart + Agent Health -->
      <VRow class="mb-1">

        <!-- Agent Health Cards -->
        <VCol cols="12" md="8">
          <VRow>
            <VCol
              v-for="(health, agent) in agentHealth"
              :key="agent"
              cols="12"
              sm="6"
            >
              <AgentStatusCard
                :agent="agent"
                :status="health.status"
                :summary="health.summary"
                :kpis="[
                  { label: t('dashboard.eventsToday'), value: health.eventsToday },
                ]"
                :last-action="health.lastAction"
              />
            </VCol>
          </VRow>
        </VCol>

        <!-- Fleet Status Donut -->
        <VCol cols="12" md="4">
          <VCard rounded="lg" class="h-100">
            <VCardItem>
              <VCardTitle class="text-body-1 font-weight-semibold">
                {{ t('dashboard.fleetStatus') }}
              </VCardTitle>
            </VCardItem>
            <VCardText style="height: 280px;">
              <VChart
                :option="donutOption"
                autoresize
                style="width: 100%; height: 100%;"
              />
            </VCardText>
          </VCard>
        </VCol>

      </VRow>

      <!-- Row 3: Mini Narrative Feed + Recent Alerts -->
      <VRow>
        <!-- Mini Narrative Feed -->
        <VCol cols="12" md="7">
          <NarrativeFeed
            compact
            :limit="10"
            height="400px"
          />
        </VCol>

        <!-- Recent Alerts -->
        <VCol cols="12" md="5">
          <VCard rounded="lg" class="h-100">
            <VCardItem>
              <VCardTitle class="text-body-1 font-weight-semibold d-flex align-center gap-2">
                <VIcon size="18" icon="mdi-alert-circle-outline" color="error" />
                {{ t('dashboard.recentAlerts') }}
              </VCardTitle>
            </VCardItem>
            <VCardText style="max-height: 400px; overflow-y: auto;">
              <div v-if="recentAlerts.length === 0" class="text-center py-8 text-disabled">
                <VIcon size="40" icon="mdi-shield-check" class="mb-2" />
                <div class="text-body-2">{{ t('dashboard.noAlerts24h') }}</div>
              </div>
              <VList v-else density="compact">
                <VListItem
                  v-for="alert in recentAlerts"
                  :key="alert.id"
                  class="px-0"
                >
                  <template #prepend>
                    <VAvatar
                      :color="alert.severity === 'critical' ? 'error' : 'warning'"
                      variant="tonal"
                      size="32"
                    >
                      <VIcon size="16" icon="mdi-alert" />
                    </VAvatar>
                  </template>
                  <VListItemTitle class="text-body-2">
                    {{ alert.title }}
                  </VListItemTitle>
                  <VListItemSubtitle class="text-caption">
                    {{ alert.asset }} · {{ formatAlertTime(alert.timestamp) }}
                  </VListItemSubtitle>
                </VListItem>
              </VList>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </div>
  </div>
</template>

<script>
// Secondary script block for helper methods used in template
export default {
  methods: {
    formatAlertTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })
    },
  },
}
</script>